import React, { useState, useMemo, useEffect, useRef } from 'react';
import Select from 'react-select';
import { collection, query, addDoc, doc, updateDoc, serverTimestamp, where, getDocs, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formOptions } from '../constants/formOptions';
import {
  formatTasaForDisplay,
  formatNumberForDisplay,
  formatNumberForInput,
  parseAmountForState,
  parseTasaForState,
  formatComitenteForDisplay,
} from '../utils/formatters';
import { useSaldosClientes } from '../hooks/useSaldosClientes';
import { useNombresComerciales } from '../hooks/useNombresComerciales';
import { softDeleteCaucion } from '../services/caucionesService';
import { comercialPorDefecto } from '../utils/rolesUsuario';
import SaldoClienteBadge from './cauciones/SaldoClienteBadge';
import Modal from './common/Modal';
import { fieldStyles } from '../config/formStyles';

/**
 * Modal reutilizable de creación/edición de cauciones.
 *
 * Controlado por el padre (isOpen/onClose). Encapsula el formulario, las opciones
 * de comitente/cliente/comercial, el preview de saldo y el guardado en Firestore
 * (incluida la deduplicación de precargadas). Al usarse como overlay desde cualquier
 * pestaña, el usuario no necesita navegar a "Cauciones" para cargar una caución.
 *
 * Props:
 * - isOpen, onClose, onSaved: control del padre. onSaved es opcional (notificación post-guardado).
 * - clientesDb, userRole, userName, userId.
 * - editingCaucion: objeto caución para editar, o null para una nueva.
 * - prefill: datos iniciales para una caución nueva. Soporta dos formas:
 *     1) Desde Control de Saldos / Dashboard: { nroComitente, nombre, moneda, saldo }
 *        → operacion según el signo del saldo, monto = |saldo|.
 *     2) Form-shaped (panel de deudores): { operacion, nroComitente, cliente, moneda, ... }.
 */
const CaucionFormModal = ({
  isOpen,
  onClose,
  onSaved,
  clientesDb,
  userRole,
  userName,
  userId,
  editingCaucion = null,
  prefill = null,
}) => {
  const { getSaldoComitente } = useSaldosClientes();

  const initialFormState = useMemo(() => ({
    operacion: '', nroComitente: '', cliente: '', dias: '', monto: 0, tasa: 0, moneda: 'Pesos', mercado: 'BYMA',
    comercial: userName,
    operador: '', estado: 'Pendiente', comentarioComercial: '', comentarioTrader: '', montoOperado: 0,
  }), [userName]);

  const [newCaucion, setNewCaucion] = useState(initialFormState);
  const [montoInputValue, setMontoInputValue] = useState('');
  const [tasaInputValue, setTasaInputValue] = useState('');
  const [formError, setFormError] = useState('');
  const didInit = useRef(false);

  const comitenteOptions = useMemo(() =>
    clientesDb ? Object.keys(clientesDb).map(c => ({ value: c, label: formatComitenteForDisplay(c) })) : [],
    [clientesDb]
  );

  const clienteOptions = useMemo(() =>
    clientesDb ? Object.entries(clientesDb).map(([value, label]) => ({ value, label: `${label} - ${formatComitenteForDisplay(value)}` })) : [],
    [clientesDb]
  );

  // Padrón de comerciales (quienes ingresaron como Comercial). Un Comercial no
  // elige: el select queda fijo en su nombre. El valor ya cargado (el del admin o
  // el de una caución vieja que se edita) siempre queda seleccionable.
  const nombresComerciales = useNombresComerciales(isOpen && userRole !== 'Comercial');
  const commercialOptions = useMemo(() => {
    const options = new Set(nombresComerciales);
    if (newCaucion.comercial) options.add(newCaucion.comercial);
    if (userRole === 'Comercial' && userName) options.add(userName);
    return [...options].sort((a, b) => a.localeCompare(b, 'es'));
  }, [nombresComerciales, newCaucion.comercial, userRole, userName]);

  // Hidrata el formulario una sola vez por apertura, según editingCaucion / prefill.
  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');

    if (editingCaucion) {
      setNewCaucion({ ...editingCaucion });
      setMontoInputValue(formatNumberForDisplay(editingCaucion.monto));
      setTasaInputValue(formatTasaForDisplay(editingCaucion.tasa));
      return;
    }

    const defaultComercial = comercialPorDefecto({ userRole, userName });

    if (prefill && prefill.saldo !== undefined) {
      // Forma desde Control de Saldos / Dashboard.
      const { nroComitente, nombre, moneda, saldo } = prefill;
      const monto = Math.abs(saldo);
      setNewCaucion({
        ...initialFormState,
        comercial: defaultComercial,
        operacion: saldo < 0 ? 'Tomadora' : 'Colocadora',
        nroComitente,
        cliente: nombre || clientesDb?.[nroComitente] || '',
        moneda,
        monto,
      });
      setMontoInputValue(monto ? formatNumberForDisplay(String(monto)) : '');
      setTasaInputValue('');
      return;
    }

    if (prefill) {
      // Forma de formulario (panel de deudores u otros).
      setNewCaucion({ ...initialFormState, comercial: defaultComercial, ...prefill });
      setMontoInputValue(prefill.monto ? formatNumberForDisplay(String(prefill.monto)) : '');
      setTasaInputValue('');
      return;
    }

    setNewCaucion({ ...initialFormState, comercial: defaultComercial });
    setMontoInputValue('');
    setTasaInputValue('');
  }, [isOpen, editingCaucion, prefill, initialFormState, userRole, userName, clientesDb]);

  const saldoModal = useMemo(() => {
    if (!isOpen) return null;
    const nroComitente = newCaucion.nroComitente;
    if (!nroComitente) return null;
    const moneda = newCaucion.moneda;
    const opts = editingCaucion?.id ? { excludeCaucionId: editingCaucion.id } : {};
    const saldoSinCaucion = getSaldoComitente(nroComitente, moneda, opts);
    if (!saldoSinCaucion) return null;
    const montoInput = parseFloat(parseAmountForState(montoInputValue)) || 0;
    const impacto = newCaucion.operacion === 'Tomadora' ? montoInput : -montoInput;
    const saldoPostCarga = saldoSinCaucion.saldoProyectado + impacto;
    return { saldoSinCaucion, saldoPostCarga, impacto, moneda };
  }, [isOpen, newCaucion.nroComitente, newCaucion.moneda, newCaucion.operacion, editingCaucion, montoInputValue, getSaldoComitente]);

  const handleNroComitenteChange = (selectedOption) => {
    const nroComitente = selectedOption ? selectedOption.value : '';
    const cliente = nroComitente && clientesDb ? clientesDb[nroComitente] : '';
    setNewCaucion({ ...newCaucion, nroComitente, cliente });
  };

  const handleClienteChange = (selectedOption) => {
    const nroComitente = selectedOption ? selectedOption.value : '';
    const cliente = nroComitente && clientesDb ? clientesDb[nroComitente] : '';
    setNewCaucion({ ...newCaucion, nroComitente, cliente });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCaucion(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCaucion.operacion) {
      setFormError('Debe seleccionar el tipo de operación (Colocadora o Tomadora).');
      return;
    }
    if (!clientesDb[newCaucion.nroComitente]) {
      setFormError('El Nro de Comitente es inválido o no existe.');
      return;
    }
    // Middle office carga en apoyo del área comercial: la caución es de un comercial.
    if (userRole === 'MiddleOffice' && !newCaucion.comercial) {
      setFormError('Seleccioná por cuenta de qué comercial cargás la caución.');
      return;
    }
    setFormError('');

    // Las cauciones no admiten decimales: el monto se redondea siempre hacia arriba (ej: 10,5 -> 11).
    const finalMonto = Math.ceil(parseFloat(parseAmountForState(montoInputValue)) || 0);
    const finalTasa = parseFloat(parseTasaForState(tasaInputValue)) || 0;

    try {
      if (editingCaucion) {
        const docRef = doc(db, "cauciones", editingCaucion.id);
        const updatePayload = {
          operacion: newCaucion.operacion,
          nroComitente: newCaucion.nroComitente,
          cliente: newCaucion.cliente,
          dias: parseInt(newCaucion.dias, 10) || 0,
          monto: finalMonto,
          tasa: finalTasa,
          moneda: newCaucion.moneda,
          mercado: newCaucion.mercado,
          comercial: userRole === 'Comercial' ? userName : newCaucion.comercial,
          comentarioComercial: newCaucion.comentarioComercial,
          estado: 'Pendiente',
          comentarioTrader: '',
          garantias: deleteField(),
          operadorId: deleteField(),
        };
        await updateDoc(docRef, updatePayload);
      } else {
        const caucionData = {
          operacion: newCaucion.operacion,
          nroComitente: newCaucion.nroComitente,
          cliente: newCaucion.cliente,
          dias: parseInt(newCaucion.dias) || 0,
          monto: finalMonto,
          tasa: finalTasa,
          moneda: newCaucion.moneda,
          mercado: newCaucion.mercado,
          comercial: userRole === 'Comercial' ? userName : newCaucion.comercial,
          comentarioComercial: newCaucion.comentarioComercial,
          createdAt: serverTimestamp(),
          creadoPorNombre: userName,
          creadoPorId: userId,
          estado: 'Pendiente',
          comentarioTrader: '',
          montoOperado: 0,
          tasaOperada: 0,
          operador: ''
        };
        await addDoc(collection(db, "cauciones"), caucionData);

        // Evita dos líneas de tomadora para el mismo comitente: si ya existe la línea
        // precargada por el cierre diario, se elimina y queda solo la recién cargada.
        // Se consulta por nroComitente (índice de campo único) y se filtra el resto en
        // memoria para no requerir un índice compuesto. El match por flag esPrecargada
        // garantiza no borrar tomadoras cargadas a mano.
        if (newCaucion.operacion === 'Tomadora') {
          const snap = await getDocs(query(collection(db, "cauciones"), where("nroComitente", "==", newCaucion.nroComitente)));
          const precargadasDuplicadas = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(c =>
              c.esPrecargada === true &&
              c.operacion === 'Tomadora' &&
              c.estado === 'Pendiente' &&
              c.moneda === newCaucion.moneda
            );
          await Promise.all(precargadasDuplicadas.map(c => softDeleteCaucion(c, { userId, userName })));
        }
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Error guardando la caución: ", error);
      setFormError("No se pudo guardar la caución.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={editingCaucion ? 'Editar Caución' : 'Nueva Caución'}
      maxWidth="720px"
      onSubmit={handleSubmit}
      error={formError}
      footer={<>
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
        <button type="submit" style={fieldStyles.button}>Guardar Caución</button>
      </>}
    >
      {saldoModal && saldoModal.saldoSinCaucion.tieneSnapshot && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
          marginBottom: '1.25rem', padding: '0.75rem',
          backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '0.375rem',
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7281', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
              Saldo actual {editingCaucion ? '(sin esta caución)' : ''}
            </div>
            <SaldoClienteBadge
              saldoReal={saldoModal.saldoSinCaucion.saldoReal}
              saldoProyectado={saldoModal.saldoSinCaucion.saldoProyectado}
              moneda={saldoModal.moneda}
              tieneSnapshot={saldoModal.saldoSinCaucion.tieneSnapshot}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7281', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
              Saldo proyectado post carga
            </div>
            <SaldoClienteBadge
              saldoReal={saldoModal.saldoPostCarga}
              saldoProyectado={saldoModal.saldoPostCarga}
              moneda={saldoModal.moneda}
              tieneSnapshot={saldoModal.saldoSinCaucion.tieneSnapshot}
            />
          </div>
        </div>
      )}
      <div className="axon-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.85rem 1.5rem' }}>
        <div><label style={fieldStyles.label}>Operación</label><select name="operacion" value={newCaucion.operacion} onChange={handleInputChange} style={fieldStyles.select}><option value="" disabled>Seleccionar...</option>{formOptions.operacion.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>

        <div>
          <label style={fieldStyles.label}>Nro Comitente</label>
          <Select
              name="nroComitente"
              options={comitenteOptions}
              value={comitenteOptions.find(c => c.value === newCaucion.nroComitente)}
              onChange={handleNroComitenteChange}
              placeholder="Buscar por Nro de Comitente..."
              isClearable
              required
              menuPortalTarget={document.body}
              styles={{
                  control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
                  ...fieldStyles.reactSelect,
              }}
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Cliente</label>
          <Select
              name="cliente"
              options={clienteOptions}
              value={clienteOptions.find(c => c.value === newCaucion.nroComitente)}
              onChange={handleClienteChange}
              placeholder="Buscar por nombre de cliente..."
              isClearable
              required
              menuPortalTarget={document.body}
              styles={{
                  control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
                  ...fieldStyles.reactSelect,
              }}
          />
        </div>

        <div><label style={fieldStyles.label}>Días</label><input type="text" inputMode="numeric" name="dias" value={newCaucion.dias} onChange={(e) => handleInputChange({ target: { name: 'dias', value: e.target.value.replace(/\D/g, '') } })} style={fieldStyles.input} required /></div>
        <div><label style={fieldStyles.label}>Monto</label><input type="text" inputMode="decimal" name="monto" value={montoInputValue} onChange={(e) => setMontoInputValue(e.target.value)} onFocus={(e) => setMontoInputValue(formatNumberForInput(e.target.value))} onBlur={(e) => { const rounded = Math.ceil(parseFloat(parseAmountForState(e.target.value)) || 0); setNewCaucion(p => ({...p, monto: rounded})); setMontoInputValue(formatNumberForDisplay(String(rounded))); }} style={fieldStyles.input} required /></div>
        <div><label style={fieldStyles.label}>Tasa (%) - Opcional</label><input type="text" inputMode="decimal" name="tasa" value={tasaInputValue} onChange={(e) => setTasaInputValue(e.target.value)} onFocus={(e) => setTasaInputValue(formatNumberForInput(e.target.value))} onBlur={(e) => { const parsed = parseTasaForState(e.target.value); setNewCaucion(p => ({...p, tasa: parseFloat(parsed) || 0})); setTasaInputValue(formatTasaForDisplay(parsed)); }} style={fieldStyles.input} placeholder="Dejar vacío si el Trader debe definir la tasa" /></div>
        <div><label style={fieldStyles.label}>Moneda</label><select name="moneda" value={newCaucion.moneda} onChange={handleInputChange} style={fieldStyles.select}>{formOptions.moneda.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
        <div><label style={fieldStyles.label}>Mercado</label><select name="mercado" value={newCaucion.mercado} onChange={handleInputChange} style={fieldStyles.select}>{formOptions.mercado.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
        <div><label style={fieldStyles.label}>Comercial</label><select name="comercial" value={newCaucion.comercial} onChange={handleInputChange} style={fieldStyles.select} disabled={userRole === 'Comercial'}>{userRole !== 'Comercial' && <option value="">Seleccione un Comercial</option>}{commercialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
        <div><label style={fieldStyles.label}>Operador</label><input type="text" name="operador" value={newCaucion.operador} style={{...fieldStyles.input, backgroundColor: '#F3F4F6'}} disabled readOnly /></div>
        <div><label style={fieldStyles.label}>Estado</label><input type="text" name="estado" value={newCaucion.estado} style={{...fieldStyles.input, backgroundColor: '#F3F4F6'}} disabled readOnly /></div>
        <div style={{ gridColumn: 'span 2' }}><label style={fieldStyles.label}>Comentario Comercial</label><textarea name="comentarioComercial" value={newCaucion.comentarioComercial} onChange={handleInputChange} style={{...fieldStyles.textarea, minHeight: '70px', backgroundColor: userRole === 'Trader' ? '#F3F4F6' : '#FFFFFF'}} disabled={userRole === 'Trader'} placeholder={userRole === 'Comercial' ? 'Ingrese un comentario...' : 'Campo deshabilitado para Traders'} ></textarea></div>
      </div>
    </Modal>
  );
};

export default CaucionFormModal;
